import type { ApiConfig } from '../api';
import type { SuperadminUser } from '../types';

export interface FetchUsersDeps {
  fetchUsers: (cfg?: ApiConfig | null) => Promise<unknown>;
  apiConfig?: ApiConfig | null;
}

function normalizeUser(user: Record<string, unknown>): SuperadminUser {
  return {
    userId: typeof user.userId === 'string' ? user.userId : undefined,
    id: typeof user.id === 'string' ? user.id : undefined,
    displayName: typeof user.displayName === 'string' ? user.displayName : undefined,
    company: typeof user.company === 'string' ? user.company : undefined,
    name: typeof user.name === 'string' ? user.name : undefined,
    email: typeof user.email === 'string' ? user.email : undefined,
    // Preserve any additional dynamic properties
    ...user,
  };
}

function sortUsersByLabel(users: SuperadminUser[]): SuperadminUser[] {
  return [...users].sort((a, b) => {
    const nameA = formatUserLabel(a).toLowerCase();
    const nameB = formatUserLabel(b).toLowerCase();
    return nameA.localeCompare(nameB);
  });
}

export async function fetchAndPopulateUsers({
  fetchUsers,
  apiConfig,
}: FetchUsersDeps): Promise<SuperadminUser[]> {
  try {
    const rawUsers = await fetchUsers(apiConfig);
    if (!Array.isArray(rawUsers)) {
      return [];
    }
    const parsedUsers = (rawUsers as Array<Record<string, unknown>>).map(normalizeUser);
    return sortUsersByLabel(parsedUsers);
  } catch {
    return [];
  }
}

export function getSelectedUserData(
  users: SuperadminUser[] | null | undefined,
  selectedUserId: string | null | undefined
): SuperadminUser | null {
  if (!Array.isArray(users)) {
    return null;
  }
  if (!selectedUserId) {
    return null;
  }
  return users.find(user => (user.userId || user.id) === selectedUserId) || null;
}

export function formatUserLabel(user: SuperadminUser): string {
  return (
    user.displayName ||
    user.company ||
    user.name ||
    user.email ||
    user.userId ||
    user.id ||
    'Unknown User'
  );
}
